import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { hasRelatedPurchaseOrders } from '@/features/lot-sizing/has-related-purchase-orders';
import { useLotSizingDraftPreview } from '@/features/lot-sizing/hooks/use-lot-sizing-draft-preview';
import { getCurrencySymbol } from '@/lib/format';
import { useGetLotSizing } from '@/services/apis/queries/lot-sizing/use-get-lot-sizing';
import { LotSizingInfoSupplier } from './lot-sizing-info-supplier';

type LotSizingInfoProps = {
  id: string;
};

export function LotSizingInfo({ id }: LotSizingInfoProps) {
  const { data: lotSizing } = useGetLotSizing(id);
  const { t } = useTranslation('lotSizing');
  const { totalAmount } = useLotSizingDraftPreview(id);

  const currency = (() => {
    const currencies = new Set<string>();
    for (const supplier of lotSizing?.suppliers ?? []) {
      if (supplier.currency) {
        currencies.add(supplier.currency);
      }
    }
    return currencies.size === 1 ? Array.from(currencies)[0] : 'N/A';
  })();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('information')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='flex flex-col gap-4'>
          <div>
            <span className='text-muted-foreground text-sm'>
              {t('suppliers')} :{' '}
            </span>
            <span className='font-normal'>
              <LotSizingInfoSupplier suppliers={lotSizing?.suppliers ?? []} />
            </span>
          </div>
          <div className='flex flex-row justify-between gap-4'>
            {hasRelatedPurchaseOrders(lotSizing) && (
              <div>
                <span className='text-muted-foreground text-sm'>
                  {t('relatedPOs')} :{' '}
                </span>
                <span className='font-medium'>
                  {lotSizing.purchaseOrders.map((po, index) => (
                    <span key={po.id}>
                      {index > 0 && ', '}
                      <Link
                        className='text-primary underline hover:text-primary/80'
                        params={{ id: po.id }}
                        to='/purchase-orders/$id'
                      >
                        {po.name}
                      </Link>
                    </span>
                  ))}
                </span>
              </div>
            )}
            <div className='ml-auto'>
              <span className='text-muted-foreground text-sm'>
                {t('lotSizingAmount')} :{' '}
              </span>
              <span className='font-medium'>
                {`${totalAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} ${getCurrencySymbol(currency)}`}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
